import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Product, ProductSummary, CartItem, ProductImage } from '@vastra/types';
import { formatCurrency } from '@vastra/utils';
import ResponsiveImage from './ResponsiveImage';

/**
 * Build a `ProductImage`-shaped object from whatever the card was handed.
 * `ProductSummary` stores its single image as `{ url, alt }` plus top-level
 * `listingUrl` / `thumbnailUrl`; full `Product.images[0]` carries the full
 * variant set. Normalize both so `<ResponsiveImage>` can pick the best one.
 */
function firstResponsiveImage(
  product: Product | ProductSummary,
): Pick<ProductImage, 'url' | 'listingUrl' | 'thumbnailUrl' | 'avifUrl' | 'avifListingUrl' | 'avifThumbnailUrl' | 'alt'> | null {
  const img = product.images[0];
  if (!img) return null;
  const summary = product as ProductSummary;
  const full = img as Partial<ProductImage>;
  return {
    url: img.url,
    listingUrl: full.listingUrl ?? summary.listingUrl,
    thumbnailUrl: full.thumbnailUrl ?? summary.thumbnailUrl,
    avifUrl: full.avifUrl,
    avifListingUrl: full.avifListingUrl,
    avifThumbnailUrl: full.avifThumbnailUrl,
    alt: img.alt || product.name,
  };
}

interface ProductCardProps {
  product: Product | ProductSummary;
  showOrigin?: boolean;
  addToCart?: (productId: string, quantity: number) => void;
  toggleWishlist?: (id: string) => void;
  wishlist?: string[];
  cartItems?: CartItem[];
  linkPrefix?: string;
  /** Override ResponsiveImage `sizes` (e.g. larger 2-col product listing). */
  imageSizes?: string;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  showOrigin = false,
  addToCart,
  toggleWishlist,
  wishlist = [],
  cartItems = [],
  linkPrefix = '/product',
  imageSizes,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [imgError, setImgError] = useState(false);
  const addToCartHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (addToCartHighlightTimerRef.current) clearTimeout(addToCartHighlightTimerRef.current);
    };
  }, []);
  const isInWishlist = wishlist.includes(product.id);
  const cartItem = cartItems.find((item) => item.productId === product.id);
  const quantityInCart = cartItem?.quantity || 0;
  const responsiveImage = firstResponsiveImage(product);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (addToCart && product.stock > 0) {
      setIsAdding(true);
      addToCart(product.id, 1);
      if (addToCartHighlightTimerRef.current) clearTimeout(addToCartHighlightTimerRef.current);
      addToCartHighlightTimerRef.current = setTimeout(() => {
        addToCartHighlightTimerRef.current = null;
        setIsAdding(false);
      }, 2000);
    }
  };

  const handleWishlistToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist?.(product.id);
  };

  return (
    <article
      className="group depth-scene flex flex-col items-center text-center"
      role="article"
      aria-label={`${product.name} - ${formatCurrency(product.price)}`}
    >
      <Link
        to={`${linkPrefix}/${product.id}`}
        className="block w-full relative focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#1f6f66] rounded-sm"
        aria-label={`View details for ${product.name}`}
      >
        <div className="depth-card aspect-[3/4] overflow-hidden mb-5 w-full relative">
          {!imgError && responsiveImage ? (
            <ResponsiveImage
              image={responsiveImage}
              variant="listing"
              sizes={imageSizes}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
              loading="lazy"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-[#8a847a]">
              <span className="text-5xl serif mb-2" aria-hidden="true">
                {product.name.charAt(0)}
              </span>
              <span className="text-[8px] font-semibold uppercase tracking-widest">
                Image Unavailable
              </span>
            </div>
          )}

          <div
            className="absolute inset-0 bg-[#14212b]/0 group-hover:bg-[#14212b]/6 transition-colors duration-500 z-[1]"
            aria-hidden="true"
          />

          {toggleWishlist && (
            <button
              onClick={handleWishlistToggle}
              aria-label={isInWishlist ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`}
              aria-pressed={isInWishlist}
              className={`absolute top-3 right-3 p-2.5 rounded-full border transition-all duration-300 z-20 ${
                isInWishlist
                  ? 'bg-[#ffffff]/95 border-[#1f6f66]/30 text-[#1f6f66]'
                  : 'bg-[#ffffff]/85 border-transparent text-[#14212b] opacity-0 group-hover:opacity-100 focus:opacity-100'
              }`}
            >
              <svg className={`w-4 h-4 ${isInWishlist ? 'fill-current' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
          )}

          {product.tags.length > 0 && (
            <div className="absolute top-3 left-3 flex flex-col gap-1.5" aria-label="Product tags">
              {product.tags.slice(0, 2).map((tag) => (
                <span
                  key={tag}
                  className="bg-[#14212b]/90 text-[#ffffff] text-[8px] font-semibold uppercase tracking-widest px-2.5 py-1 z-10"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {quantityInCart > 0 && !isAdding && (
            <div className="absolute bottom-2 left-2 bg-[#ffffff]/95 border border-[#14212b]/10 px-3 py-1.5 z-10 pointer-events-none" aria-live="polite">
              <span className="text-[9px] font-semibold uppercase tracking-widest text-[#14212b]">
                In Bag ({quantityInCart})
              </span>
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 translate-y-0 md:translate-y-full md:group-hover:translate-y-0 transition-transform duration-500 ease-out z-20">
            <button
              onClick={handleAddToCart}
              disabled={product.stock <= 0}
              aria-label={product.stock <= 0 ? `${product.name} is out of stock` : `Add ${product.name} to bag`}
              className={`w-full py-3.5 text-[10px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 flex items-center justify-center gap-2 ${
                isAdding
                  ? 'bg-[#1f6f66] text-[#ffffff]'
                  : product.stock <= 0
                    ? 'bg-[#e8eef1] text-[#8a847a] cursor-not-allowed'
                    : 'bg-[#ffffff] text-[#14212b] hover:bg-[#1f6f66] hover:text-[#ffffff]'
              }`}
            >
              {isAdding ? (
                <>Added to bag</>
              ) : product.stock <= 0 ? (
                'Out of stock'
              ) : quantityInCart > 0 ? (
                `Add another (${quantityInCart})`
              ) : (
                'Add to bag'
              )}
            </button>
          </div>
        </div>

        <div className="space-y-1.5 px-2 pb-2">
          <span className="notranslate text-[10px] font-semibold text-[#1f6f66] uppercase tracking-[0.16em]">
            {product.category}
            {showOrigin && product.origin && ` · ${product.origin}`}
          </span>
          <h3 className="notranslate font-medium text-[#14212b] tracking-wide text-sm group-hover:text-[#1f6f66] transition-colors leading-snug">
            {product.name}
          </h3>
          <p className="text-sm font-semibold text-[#2a3a48] mt-1" aria-label={`Price: ${formatCurrency(product.price)}`}>
            {formatCurrency(product.price)}
            {product.compareAtPrice && product.compareAtPrice > product.price && (
              <span className="ml-2 text-xs font-normal text-[#8a847a] line-through">
                {formatCurrency(product.compareAtPrice)}
              </span>
            )}
          </p>
        </div>
      </Link>
    </article>
  );
};

export default ProductCard;
